import { type EventContentObjectDetail, ProgramInviteType } from 'models';
import { Form, FormActions, type FormInstance, Modal } from 'ui';
import { type CommonFormValues, EventInviteesPicker, useEventInvitees } from '../../event-form-modal';
import type { RequiredFormValues } from '../../event-form-modal/data-step-event-form/event-invitees-picker';
import { EventInviteesSelects } from '../../event-invitees-selects';

type DefaultEventFormData = {
  formValues: DefaultEventFormValues;
};

type DefaultEventFormValues = CommonFormValues;

type P = {
  eventContentObject: EventContentObjectDetail;
  inviteType: ProgramInviteType;
  isUpdating: boolean;
  onBack: () => void;
  onInviteesTypeChanged: (type: ProgramInviteType) => void;
  onSubmit: (data: DefaultEventFormData) => void;
};

const DefaultEventForm = ({
  eventContentObject,
  inviteType,
  isUpdating,
  onBack,
  onInviteesTypeChanged,
  onSubmit,
}: P) => {
  const { id: eventId, viewPermission } = eventContentObject.event;
  const [form] = Form.useForm<DefaultEventFormValues>();

  const { onInvite, onShowInvitees } = useEventInvitees<DefaultEventFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Antd interface need to be casted to the correct type
    form: form as unknown as FormInstance<RequiredFormValues>,
    onInviteesTypeChanged,
    skipPostInCommunityValidation: true,
  });

  const onFinish = (formValues: DefaultEventFormValues) => {
    onSubmit({ formValues });
  };

  return (
    <>
      {inviteType ? (
        <EventInviteesPicker
          form={form}
          inviteType={inviteType}
          onBack={onBack}
          onInvite={onInvite}
          programId={eventContentObject.contentableType === 'Program' ? eventContentObject.program.id : undefined}
        />
      ) : (
        <Form<DefaultEventFormValues> form={form} onFinish={onFinish}>
          <EventInviteesSelects<DefaultEventFormValues>
            selects={[
              {
                buttonText: 'Invite',
                key: 'moderator',
                label: 'Admins / Hosts',
                onPress: onShowInvitees(ProgramInviteType.moderators),
                roleType: 'moderator',
              },
              {
                buttonText: 'Invite',
                inviteOptions: {
                  eventId,
                  kind: 'event_presenter_invitation',
                  successMessage: 'Link for presenter copied successfully',
                },
                key: 'presenter',
                label: 'Presenters',
                onPress: onShowInvitees(ProgramInviteType.presenters),
                roleType: 'presenter',
              },
              {
                buttonText: 'Invite',
                disabled: viewPermission === 'public',
                inviteOptions: {
                  eventId,
                  kind: 'event_attendee_invitation',
                  successMessage: 'Link for attendee copied successfully',
                },
                key: 'attendee',
                label: 'Attendees',
                onPress: onShowInvitees(ProgramInviteType.invitees),
                roleType: 'attendee',
              },
            ]}
          />
          <Modal.HugContent.Footer>
            <FormActions
              submit={{
                id: 'btn_invite_users',
                loading: isUpdating,
              }}
            />
          </Modal.HugContent.Footer>
        </Form>
      )}
    </>
  );
};

export default DefaultEventForm;
